/**
 * "Sold by", chosen from the sellers the catalog actually has: providers that
 * bill for some publisher's models, not every publisher.
 *
 * It was a free-text box matched exactly against provider IDs, so "Anthropic"
 * found nothing and the empty table suggested nothing had been collected.
 */

import { useQuery } from "@tanstack/react-query";

import type { Provider } from "@pricing/contracts";

import { client } from "../lib/api.ts";

export function SellerSelect({
  value,
  onChange,
}: {
  readonly value: string;
  readonly onChange: (value: string) => void;
}): React.JSX.Element {
  const providers = useQuery({
    queryKey: ["sellers"],
    staleTime: 5 * 60_000,
    // Every page: the list defaults to 50 providers, and a real catalog has
    // more than that once publishers are counted.
    queryFn: async () => {
      const all: Provider[] = [];
      let cursor: string | null = null;
      do {
        const page: Awaited<ReturnType<typeof client.providers>> = await client.providers(
          (cursor ? { cursor } : { limit: 500 }) as never,
        );
        all.push(...page.data);
        cursor = page.page?.next_cursor ?? null;
      } while (cursor !== null);
      return all;
    },
  });
  // Sellers only. Providers include every publisher, and choosing one that
  // sells nothing (meta, deepseek) led straight back to an empty table.
  const ids = (providers.data ?? [])
    .filter((provider) => (provider.publishes ?? []).length > 0)
    .map((provider) => provider.provider_id);

  return (
    <label className="field">
      Sold by
      <select value={value} onChange={(event) => onChange(event.target.value)}>
        <option value="">All sellers</option>
        {ids.map((id) => (
          <option key={id} value={id}>
            {id}
          </option>
        ))}
        {/* A shared URL can name a seller this catalog does not have: say so,
            rather than silently showing "All sellers" over an empty table. */}
        {value !== "" && providers.isSuccess && !ids.includes(value) ? (
          <option value={value}>{value} (not in this catalog)</option>
        ) : null}
      </select>
    </label>
  );
}
